import { Seo } from "../components/Seo";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  Plane, LayoutDashboard, Shield, LogOut, LogIn, FileText, Palette,
  Zap, ScanLine, ImageIcon, Layers, Share2, Code2, Check, ArrowRight, Sparkles,
  Sun, Moon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import { LayoutGallery } from "../components/LayoutGallery";
import PnrConverter from "../components/PnrConverter";
import { ContactForm } from "../components/ContactForm";

const HERO_BG =
  "https://images.unsplash.com/photo-1544590907-d36afe5aeb96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxhaXJwbGFuZSUyMHdpbmclMjBhYm92ZSUyMGNsb3VkcyUyMGRhcmslMjBza3l8ZW58MHx8fHwxNzkwMTU1ODAzfDA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Theme token maps — the toggle only affects this landing page.
const THEMES = {
  dark: {
    page: "bg-[#05050A] text-white",
    navScrolled: "bg-[#05050A]/80 backdrop-blur-xl border-b border-white/5",
    navLink: "text-slate-300 hover:text-white",
    brandTitle: "text-white",
    brandSub: "text-slate-400",
    heroImgOpacity: 0.18,
    heroGlow: "bg-[radial-gradient(ellipse_at_top,_rgba(0,122,255,0.18),_rgba(5,5,10,0.85)_55%,_#05050A_100%)]",
    pill: "border-white/10 bg-white/5 text-[#00F0FF]",
    overline: "text-[#00F0FF]",
    heading: "text-white",
    gradientText: "bg-gradient-to-r from-[#00F0FF] to-[#007AFF]",
    sub: "text-slate-300",
    subMuted: "text-slate-400",
    ctaSecondary: "border-white/15 bg-transparent text-white hover:bg-white/5",
    check: "text-[#00F0FF]",
    toolWrapper: "bg-[#0D0E15] border border-[#007AFF]/30 shadow-[0_0_50px_rgba(0,122,255,0.15)]",
    sectionBorder: "border-white/5",
    card: "bg-[#0D0E15] border border-white/5 hover:border-[#00F0FF]/30 hover:shadow-[0_8px_32px_rgba(0,240,255,0.06)]",
    iconBadge: "bg-[#00F0FF]/10 drop-shadow-[0_0_12px_rgba(0,240,255,0.4)]",
    icon: "text-[#00F0FF]",
    stepNumber: "text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.15)]",
    stepConnector: "border-white/15",
    proCard: "bg-gradient-to-b from-[#0D0E15] to-[#151722] border border-[#007AFF] shadow-[0_0_40px_rgba(0,122,255,0.2)]",
    sideCard: "bg-[#0D0E15] border border-white/5 hover:border-[#00F0FF]/30",
    priceText: "text-white",
    featureLi: "text-slate-300",
    sideCta: "bg-white/5 hover:bg-white/10 text-white border border-white/10",
    toggleBtn: "border-white/15 text-slate-200 hover:bg-white/5 hover:text-white",
    footerText: "text-slate-400 hover:text-white",
    footerMuted: "text-slate-500",
  },
  light: {
    page: "bg-[#F6F8FC] text-slate-900",
    navScrolled: "bg-white/80 backdrop-blur-xl border-b border-slate-200",
    navLink: "text-slate-600 hover:text-slate-900",
    brandTitle: "text-slate-900",
    brandSub: "text-slate-500",
    heroImgOpacity: 0.12,
    heroGlow: "bg-[radial-gradient(ellipse_at_top,_rgba(0,122,255,0.14),_rgba(246,248,252,0.85)_55%,_#F6F8FC_100%)]",
    pill: "border-slate-200 bg-white text-[#0284C7] shadow-sm",
    overline: "text-[#0284C7]",
    heading: "text-slate-900",
    gradientText: "bg-gradient-to-r from-[#007AFF] to-[#00B8D4]",
    sub: "text-slate-600",
    subMuted: "text-slate-500",
    ctaSecondary: "border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
    check: "text-[#007AFF]",
    toolWrapper: "bg-white border border-slate-200 shadow-[0_12px_44px_rgba(2,132,199,0.12)]",
    sectionBorder: "border-slate-200",
    card: "bg-white border border-slate-200 hover:border-[#007AFF]/40 hover:shadow-[0_12px_32px_rgba(2,132,199,0.10)]",
    iconBadge: "bg-[#007AFF]/10",
    icon: "text-[#007AFF]",
    stepNumber: "text-transparent [-webkit-text-stroke:1px_rgba(15,23,42,0.18)]",
    stepConnector: "border-slate-300",
    proCard: "bg-white border-2 border-[#007AFF] shadow-[0_16px_48px_rgba(2,132,199,0.18)]",
    sideCard: "bg-white border border-slate-200 hover:border-[#007AFF]/40",
    priceText: "text-slate-900",
    featureLi: "text-slate-600",
    sideCta: "bg-slate-100 hover:bg-slate-200 text-slate-900 border border-slate-200",
    toggleBtn: "border-slate-300 text-slate-700 hover:bg-slate-100",
    footerText: "text-slate-500 hover:text-slate-900",
    footerMuted: "text-slate-400",
  },
};

function LandingNavbar({ c, dark, onToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = `text-sm font-medium px-3 py-1.5 rounded-md transition-colors inline-flex items-center gap-1.5 ${c.navLink}`;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? c.navScrolled : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center" data-testid="nav-brand">
          <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-11 w-auto object-contain" />
        </Link>

        <nav className="flex items-center gap-1">
          <button data-testid="nav-convert" onClick={() => scrollTo("converter-tool")} className={linkCls}>Convert</button>
          <button data-testid="nav-pricing" onClick={() => navigate("/pricing")} className={`hidden sm:inline-flex ${linkCls}`}>Pricing</button>
          <Link to="/developers" data-testid="nav-developers" className={linkCls}>API Docs</Link>
          {user && (
            <Link to="/dashboard" data-testid="nav-dashboard" className={`hidden md:inline-flex ${linkCls}`}>
              <LayoutDashboard className="h-4 w-4" /> Dashboard
            </Link>
          )}
          {user && (
            <Link to="/itineraries" data-testid="nav-itineraries" className={`hidden md:inline-flex ${linkCls}`}>
              <FileText className="h-4 w-4" /> Itineraries
            </Link>
          )}
          {user && (
            <Link to="/branding" data-testid="nav-branding" className={`hidden lg:inline-flex ${linkCls}`}>
              <Palette className="h-4 w-4" /> Branding
            </Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className={`hidden lg:inline-flex ${linkCls}`}>
              <Shield className="h-4 w-4" /> Admin
            </Link>
          )}

          <button
            data-testid="theme-toggle"
            onClick={onToggle}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
            className={`ml-1 h-9 w-9 inline-flex items-center justify-center rounded-full border transition-colors ${c.toggleBtn}`}
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <div className="ml-2 flex items-center gap-2">
            {user ? (
              <Button
                data-testid="logout-btn" variant="outline" size="sm"
                className={`bg-transparent ${c.toggleBtn}`}
                onClick={() => { logout(); navigate("/login"); }}
              >
                <LogOut className="h-4 w-4 mr-1.5" /> Logout
              </Button>
            ) : (
              user === false && (
                <>
                  <Button data-testid="nav-login-btn" variant="ghost" size="sm" onClick={() => navigate("/login")} className={c.navLink}>
                    <LogIn className="h-4 w-4 mr-1.5" /> Login
                  </Button>
                  <Button
                    data-testid="nav-signup-btn" size="sm"
                    className="rounded-full bg-[#007AFF] hover:bg-[#00F0FF] text-white hover:text-black font-semibold transition-colors"
                    onClick={() => navigate("/register")}
                  >
                    Sign up
                  </Button>
                </>
              )
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

const FEATURES = [
  { icon: ScanLine, title: "Sabre & Amadeus parsing", desc: "Paste raw GDS text from either format — we auto-detect and decode every segment accurately.", span: "md:col-span-4" },
  { icon: ImageIcon, title: "Automatic airline logos", desc: "Live logo fetching with a clean IATA-code fallback, so every itinerary looks polished.", span: "md:col-span-4" },
  { icon: Sparkles, title: "SSR, fares, seats & e-tickets", desc: "Extract remarks, fare details, baggage allowance, seat numbers, e-tickets and change/cancel conditions.", span: "md:col-span-4" },
  { icon: Layers, title: "Drag & drop branded builder", desc: "Combine flights with hotels, transfers and tours. Add your agency logo, colors and themes.", span: "md:col-span-4" },
  { icon: Share2, title: "Export & share anywhere", desc: "Download as PDF or HTML, send as WhatsApp text or image, or share a password-protected link.", span: "md:col-span-4" },
  { icon: Code2, title: "Public REST API", desc: "Integrate PNR conversion into your own product with API keys and usage-based plans.", span: "md:col-span-4" },
];

const STEPS = [
  { n: "01", title: "Paste your PNR", desc: "Drop raw Sabre or Amadeus itinerary text into the converter." },
  { n: "02", title: "Convert instantly", desc: "We decode segments, airlines, airports and extra fields in a click." },
  { n: "03", title: "Customize & brand", desc: "Reorder blocks, add extras and apply your agency branding." },
  { n: "04", title: "Share or export", desc: "Send a link, PDF, HTML or WhatsApp-ready itinerary to your client." },
];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const [dark, setDark] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("pnr_home_theme") : null;
    return saved ? saved === "dark" : false; // default: light
  });
  useEffect(() => {
    localStorage.setItem("pnr_home_theme", dark ? "dark" : "light");
  }, [dark]);

  const [chosenLayout, setChosenLayout] = useState(null);

  const c = dark ? THEMES.dark : THEMES.light;

  // Gentle hero parallax (disabled when the user prefers reduced motion).
  const { scrollY } = useScroll();
  const rawY = useTransform(scrollY, [0, 700], [0, 140]);
  const rawScale = useTransform(scrollY, [0, 700], [1, 1.12]);
  const heroY = reduce ? 0 : rawY;
  const heroScale = reduce ? 1 : rawScale;

  // Entrance animation props, respecting reduced-motion.
  const fade = (delay = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-80px" },
          transition: { duration: 0.6, delay, ease: "easeOut" },
        };

  // While auth state resolves, avoid flashing the wrong view.
  if (user === null) {
    return <div className="min-h-screen bg-slate-50" data-testid="home-loading" />;
  }

  // Logged-in users get the focused converter workspace, not the marketing homepage.
  if (user) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="converter-workspace">
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-10">
          {!chosenLayout ? (
            <div data-testid="layout-step">
              <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Step 1 of 2</div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Choose a layout</h1>
                <p className="text-slate-500 mt-1">Pick a template for your itinerary. Preview each one below — you can change it later in the builder.</p>
              </div>
              <LayoutGallery selectedId={chosenLayout?.id} onSelect={setChosenLayout} />
            </div>
          ) : (
            <div data-testid="convert-step">
              <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Step 2 of 2</div>
                  <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Create Itinerary</h1>
                  <p className="text-slate-500 mt-1">Paste a Sabre or Amadeus PNR below, convert it, then save it as a branded itinerary.</p>
                </div>
                <button
                  onClick={() => setChosenLayout(null)}
                  data-testid="change-layout-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Layers className="h-4 w-4 text-blue-600" />
                  Layout: <span className="font-semibold text-slate-900">{chosenLayout.name}</span>
                  <span className="text-blue-600 font-medium">· Change</span>
                </button>
              </div>
              <PnrConverter layout={chosenLayout.id} />
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen antialiased overflow-x-hidden transition-colors duration-300 ${c.page}`} data-testid="home-root" data-theme={dark ? "dark" : "light"}>
      <Seo path="/" title="Convert GDS PNRs into branded travel itineraries" description="Paste raw Sabre or Amadeus GDS text and instantly decode flights, hotels, cars, cruises, excursions, SSRs, seats, e-tickets, meals, fares and passenger details into a clean, branded, customer-ready itinerary you can share or export as PDF." />
      <LandingNavbar c={c} dark={dark} onToggle={() => setDark((v) => !v)} />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex flex-col items-center justify-center text-center px-6 pt-28 pb-16 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover bg-center will-change-transform"
          style={{ backgroundImage: `url(${HERO_BG})`, opacity: c.heroImgOpacity, y: heroY, scale: heroScale }}
          aria-hidden
        />
        <div className={`absolute inset-0 ${c.heroGlow}`} aria-hidden />
        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center">
          <motion.div {...fade(0)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] ${c.pill}`}>
            <Sparkles className="h-3.5 w-3.5" /> Now decoding hotels, cars, cruises &amp; more
          </motion.div>
          <motion.h1 {...fade(0.05)} className={`mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none ${c.heading}`}>
            Turn cryptic GDS PNRs into{" "}
            <span className={`${c.gradientText} bg-clip-text text-transparent`}>beautiful travel itineraries</span>
          </motion.h1>
          <motion.p {...fade(0.12)} className={`mt-6 max-w-2xl text-base sm:text-lg leading-relaxed ${c.sub}`}>
            Paste raw Sabre or Amadeus GDS text and instantly transform complex cryptic PNR data into a
            clean, branded, customer-friendly itinerary.
          </motion.p>
          <motion.div {...fade(0.16)} className="mt-5 flex flex-wrap items-center justify-center gap-1.5 max-w-2xl">
            {["Flights", "Hotels", "Cars", "Cruises", "Excursions", "Sightseeing", "SSRs", "Seats", "E-Tickets", "Meals", "Fares", "Passenger Details"].map((tag) => (
              <span key={tag} className={`rounded-full border px-3 py-1 text-xs font-semibold ${c.pill}`}>{tag}</span>
            ))}
          </motion.div>
          <motion.p {...fade(0.2)} className={`mt-5 text-sm sm:text-base font-medium ${c.subMuted}`}>
            No manual formatting. No confusing cryptic codes. Just a beautiful itinerary ready to share with your customer.
          </motion.p>
          <motion.div {...fade(0.18)} className="mt-9 flex flex-col sm:flex-row items-center gap-3">
            <Button
              data-testid="hero-convert-btn"
              onClick={() => scrollTo("converter-tool")}
              className="rounded-full px-7 h-12 text-base bg-[#007AFF] hover:bg-[#00F0FF] text-white hover:text-black font-semibold transition-[transform,background-color,color,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_0_24px_rgba(0,240,255,0.45)]"
            >
              <Zap className="h-5 w-5 mr-2" /> Convert a PNR
            </Button>
            <Button
              data-testid="hero-pricing-btn"
              onClick={() => navigate("/pricing")}
              variant="outline"
              className={`rounded-full px-7 h-12 text-base transition-[transform,background-color] duration-200 hover:scale-[1.03] ${c.ctaSecondary}`}
            >
              View pricing <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>
          <motion.div {...fade(0.24)} className={`mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm ${c.subMuted}`}>
            <span className="inline-flex items-center gap-2"><Check className={`h-4 w-4 ${c.check}`} /> Sabre &amp; Amadeus</span>
            <span className="inline-flex items-center gap-2"><Check className={`h-4 w-4 ${c.check}`} /> No credit card to start</span>
            <span className="inline-flex items-center gap-2"><Check className={`h-4 w-4 ${c.check}`} /> Free forever tier</span>
          </motion.div>
        </div>
      </section>

      {/* CONVERTER TOOL */}
      <section id="converter-tool" className="scroll-mt-20 py-20 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade(0)} className="text-center mb-10">
            <div className={`text-xs font-bold uppercase tracking-[0.2em] ${c.overline}`}>Live tool</div>
            <h2 className={`mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight ${c.heading}`}>Try the converter now</h2>
            <p className={`mt-3 max-w-xl mx-auto ${c.subMuted}`}>Paste a PNR on the left and watch it become a clean itinerary on the right. No sign-up required.</p>
          </motion.div>
          <motion.div {...fade(0.08)} className={`rounded-3xl p-2 sm:p-4 ${c.toolWrapper}`}>
            <PnrConverter dark={dark} />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className={`py-20 sm:py-28 px-6 border-t ${c.sectionBorder}`}>
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade(0)} className="max-w-2xl">
            <div className={`text-xs font-bold uppercase tracking-[0.2em] ${c.overline}`}>Why PNR Decode</div>
            <h2 className={`mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight ${c.heading}`}>Everything you need to convert, brand &amp; share</h2>
            <p className={`mt-3 ${c.subMuted}`}>Built for travel agents who want to spend less time formatting and more time selling.</p>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                {...fade(i * 0.05)}
                data-testid={`feature-card-${i}`}
                className={`${f.span} rounded-2xl p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 ${c.card}`}
              >
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${c.iconBadge}`}>
                  <f.icon className={`h-5 w-5 ${c.icon}`} />
                </div>
                <h3 className={`mt-5 font-heading text-xl font-semibold ${c.heading}`}>{f.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${c.subMuted}`}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className={`py-20 sm:py-28 px-6 border-t ${c.sectionBorder}`}>
        <div className="max-w-7xl mx-auto">
          <motion.div {...fade(0)} className="text-center max-w-2xl mx-auto">
            <div className={`text-xs font-bold uppercase tracking-[0.2em] ${c.overline}`}>How it works</div>
            <h2 className={`mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight ${c.heading}`}>From raw text to ready in four steps</h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fade(i * 0.08)} data-testid={`step-${i}`} className="relative">
                <div className={`font-heading text-6xl font-black ${c.stepNumber}`}>{s.n}</div>
                <h3 className={`mt-4 font-heading text-xl font-semibold ${c.heading}`}>{s.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${c.subMuted}`}>{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <div className={`hidden lg:block absolute top-7 right-[-1.25rem] w-10 border-t border-dashed ${c.stepConnector}`} />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <ContactForm dark={dark} />

      {/* FOOTER */}
      <footer className={`border-t px-6 py-12 ${c.sectionBorder}`}>
        <div className="max-w-7xl mx-auto flex flex-col gap-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-9 w-auto object-contain" />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <button onClick={() => scrollTo("converter-tool")} className={`transition-colors ${c.footerText}`}>Convert</button>
              <Link to="/pricing" className={`transition-colors ${c.footerText}`}>Pricing</Link>
              <Link to="/developers" className={`transition-colors ${c.footerText}`}>API Docs</Link>
              <button onClick={() => scrollTo("contact")} className={`transition-colors ${c.footerText}`}>Contact</button>
              <a href="mailto:support@pnrdecode.com" className={`transition-colors ${c.footerText}`}>support@pnrdecode.com</a>
            </div>
          </div>
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-6 ${c.sectionBorder}`}>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
              <Link to="/privacy-policy" className={`transition-colors ${c.footerText}`}>Privacy Policy</Link>
              <Link to="/terms-of-use" className={`transition-colors ${c.footerText}`}>Terms of Use</Link>
              <Link to="/cookies-policy" className={`transition-colors ${c.footerText}`}>Cookie Policy</Link>
              <Link to="/refund-policy" className={`transition-colors ${c.footerText}`}>Refund Policy</Link>
            </div>
            <div className={`text-xs ${c.footerMuted}`}>© {new Date().getFullYear()} PNR Decode. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
